import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';

import { getMixersList } from '../../entities/mixers/mixers.selector';
import { Forms } from '../../form/form.selector';
import { RootState } from '../../reducers';
import { LoadListPosition, LoadMixer, LoadMixerPremix } from './loadMixers.types';
import { findRecipeById, getRecipes } from '../../entities/recipes/recipes.selector';
import { getPremixes, isRationAPremixFn } from '../../entities/premixes/premixes.selector';
import { FeedingPeriod } from '../../entities/feeding/feeding.types';
import { getAllGroups } from '../../entities/group/group.selector';
import { getOtherSettings } from '../../entities/settings/settings.selector';
import { Group } from '../../entities/group/group.types';
import { OtherSettings } from '../../entities/settings/settings.types';

const loadMixersForm = formValueSelector(Forms.loadMixersForm);
export const feedingPeriodFormList = (state: RootState) => loadMixersForm(state, 'feeding');

export const getLoadMixers = (state: RootState): LoadMixer[] => state.application.loadMixers.feedingLoads;

export const getLoadMixerPremixes = (state: RootState): LoadMixerPremix[] => state.application.loadMixers.premixes;

export const getDataTransferMethod = (state: RootState) => state.application.loadMixers.dataTransferMethod;

export const getEffectiveDate = (state: RootState) => state.application.loadMixers.effectiveDate;

export const getLoadMixerPremixesInfo = createSelector(
  [getLoadMixerPremixes, getAllGroups, getOtherSettings],
  (loadMixerPremixes: LoadMixerPremix[], groups: Group[], settings: OtherSettings) => {
    const isPerc = settings.premixAmountsByPercentage;
    return loadMixerPremixes.map((x) => {
      const totalReq = x.totalDailyRequirement;
      const totalScheduled = x.totalScheduledAmount;
      const group = groups.find((g) => g.id === x.groupId);
      const numberOfFeedings = group ? group.numberOfFeedings : 0;

      const isRedWarning = totalReq > 0 && totalScheduled < totalReq;
      const isOrangeWarning = !isPerc && numberOfFeedings > 0 && totalReq === 0 && totalScheduled > 0;

      return {
        ...x,
        redWarning: isRedWarning,
        orangeWarning: isOrangeWarning,
      };
    });
  },
);

export const isPremixesHasWarnings = createSelector(getLoadMixerPremixesInfo, (premixes) =>
  premixes.some((x) => x.orangeWarning || x.redWarning),
);

export const getSelectedMixersScaleNumber = (state: RootState): number[] =>
  state.application.loadMixers.selectedMixersScaleNumber;

export const hasLoads = createSelector(getLoadMixers, (loadMixers): boolean => !!loadMixers.length);

export const hasSelectedFeedingPeriods = createSelector(
  feedingPeriodFormList,
  (feedingPeriodList) => !!Object.values(feedingPeriodList || []).find((checked) => !!checked),
);

export const hasAtLeastOneMixerSelected = createSelector(
  getSelectedMixersScaleNumber,
  (selectedMixers) => !!selectedMixers.length && selectedMixers.length >= 1,
);

export const getSelectedMixers = createSelector(
  [getMixersList, getSelectedMixersScaleNumber],
  (mixerList, selectedMixersScaleNumber) =>
    mixerList.filter((mixer) => mixer.loadSize && selectedMixersScaleNumber.includes(mixer.scaleNumber)),
);

export const getAvailableMixerList = createSelector(
  [getMixersList, getSelectedMixers],
  (mixerList, selectedMixerList) => {
    const selectedMixersIds = selectedMixerList.map((mixer) => mixer.id);
    return mixerList.filter((mixer) => mixer.statusCode !== 4 && !selectedMixersIds.includes(mixer.id));
  },
);

export const getSelectedScheduledPremix = (state: RootState): string | null =>
  state.application.loadMixers.selectedScheduledPremix;

export const getLoadMixerPremixesByRationId = createSelector(
  [getLoadMixerPremixes, (state: RootState, rationId: string | null) => rationId],
  (loadMixerPremixesList, rationId) => {
    return loadMixerPremixesList.find((premix) => premix.rationId === rationId);
  },
);

export const getLoadByPenId = createSelector(
  [getLoadMixers],
  (loads) => (groupId: string, feedingNo: number) =>
    loads.find(
      (load) => load.feedingNo === feedingNo && load.groups.findIndex((group) => group.groupId === groupId) !== -1,
    ),
);

export const getState = createSelector(
  [getLoadMixerPremixes, (state: RootState, rationId: string) => rationId],
  (loadMixerPremixesList, rationId) => {
    return loadMixerPremixesList.find((premix) => premix.rationId === rationId);
  },
);

export const getMappedLoadMixers = createSelector(
  [getLoadMixers, getRecipes, getPremixes],
  (loadMixers, recipes, premixes): LoadMixer[] => {
    return loadMixers.map((load) => {
      const recipe: any = findRecipeById(recipes, premixes, load.rationId);
      const isPremix = isRationAPremixFn(premixes, load.rationId);
      return {
        ...load,
        density: recipe.density,
        isPremix,
      };
    });
  },
);

export const getCheckedFeedingPeriods = createSelector([feedingPeriodFormList], (feedingPeriodList) => {
  const periods = Object.keys(feedingPeriodList || {}).reduce((acc: any[], curr) => {
    const checked = feedingPeriodList[curr];
    const newObj = {
      feedingNo: Number(curr.substr(1)), // example output feeding.$1 remove first char $ because of redux-form bug naming field as name.index
      checked,
    };
    return acc.concat(newObj);
  }, []);
  return periods.filter((period) => period.checked).map((period) => period.feedingNo);
});

export const getFeedingsScheduling = (state: RootState) => state.application.loadMixers.feedingsScheduling;

export const getAvailableFeedingPeriodId = createSelector(
  [getFeedingsScheduling, (_state: RootState, feedingPeriods: FeedingPeriod[]) => feedingPeriods],
  (feedings, feedingPeriods) => {
    const feedingPeriod = feedingPeriods.find((x) => feedings.every((f) => f.feedingPeriodId !== x.id));
    return feedingPeriod && feedingPeriod.id;
  },
);

export const getActiveFeedingLoad = (state: RootState) => state.application.loadMixers.activeFeedingLoad;
export const getActiveFeedingLoadGroup = (state: RootState) => state.application.loadMixers.activeFeedingLoadGroup;
export const getActiveFeedingGroup = (state: RootState) => state.application.loadMixers.activeFeedingGroup;
export const getActiveDraggingFeedingGroup = (state: RootState) =>
  state.application.loadMixers.activeDraggingFeedingGroup;
export const getLoadListDragPosition = (state: RootState): LoadListPosition | null => {
  const dragState = getActiveDraggingFeedingGroup(state);
  if (!dragState) return null;

  const feedingPeriods = state.entities.feeding.feedingPeriods;
  const feedingPeriod = feedingPeriods.find((x) => (x.feedings || []).some((f) => f.id === dragState.feedingId));

  if (!feedingPeriod) return null;

  const feedings = feedingPeriod.feedings || [];
  const loads = getLoadMixers(state).filter((x) => x.feedingNo === feedingPeriod.feedingNo);
  const startIndex = feedings.findIndex((x) => x.id === dragState.feedingId);
  const startFindIndex = startIndex <= dragState.newPosition ? dragState.newPosition + 1 : dragState.newPosition;

  for (let i = startFindIndex; i < feedings.length; i += 1) {
    const feeding = feedings[i];
    const load = loads.find((x) => x.groups.some((g) => g.groupId === feeding.groupId));
    if (load) {
      return {
        loadUUID: load.uuid,
        groupId: feeding.groupId,
      };
    }
  }

  return {
    loadUUID: loads[loads.length - 1].uuid,
    after: true,
  };
};

export const getAutoExpandLoad = (state: RootState) => state.application.loadMixers.autoExpandLoad;
export const getActiveDraggingFeedingId = (state: RootState) => {
  const draggingFeedingGroup = getActiveDraggingFeedingGroup(state);
  const feedingId = draggingFeedingGroup ? draggingFeedingGroup.feedingId : null;
  return feedingId;
};
